import React, { useState, useEffect, useContext } from "react"
import { db, fun, auth, storage } from "../../../utils/firebase"
import { AuthContext } from "../../../utils/providers/auth"
import { isSuperAdminAllowlisted } from "../../../utils/exports/super-admin-allowlist"
import firebase from "firebase"
import "./staff.scss"

// UI components
import WindowCard from "../../ui/window-card/window-card"
import Window from "../../structure/window/window"
import Checkbox from "../../ui/checkbox/checkbox"
import Textarea from "../../ui/inputs/textarea"
import Button from "../../ui/button/button"
import Input from "../../ui/inputs/input"
import File from "../../ui/inputs/file"
import Select from "../../ui/select/select"
import { UserIcon, PenIcon, GlobeIcon, ImageIcon, EyeIcon, CopyIcon, CancelIcon, UndoIcon } from "../../../utils/svgs"

// Required to generate the ID for the new content document
const random = require("randomstring")

const managers = {
	M1EUhnht2mQc9pucFAB7A8rWFul1: "Jack Allen",
	"8lIXQuliWhPJXrgi0rM7dJ3e9kM2": "James Bilotta",
	gWd7pXtuAMcXrywMUTw2vRbid1j1: "Josh Drury",
	p4VG2EgFFQS8fsaBc0d9peNWaD52: "Matt Hill",
	L334Rwj0YiRWSuCZLlixoBbsxwl2: "Oliver Howell",
}

// Functional component to return staff table row with targets
export default function Staff(props) {
	const [saving, setSaving] = useState(false)

	const [savingBio, setSavingBio] = useState(false)
	const [savingEmail, setSavingEmail] = useState(false)
	const [settingBio, setSettingBio] = useState(false)
	const [newEmail, setNewEmail] = useState("")
	const [bio, setBio] = useState("")
	const [manager, setManager] = useState({})
	const [clearingManager, setClearingManager] = useState(false)

	const [savingPhone, setSavingPhone] = useState(false)
	const [settingPhone, setSettingPhone] = useState(false)
	const [changeEmail, setChangeEmail] = useState(false)
	const [phone, setPhone] = useState("")

	const [savingPicture, setSavingPicture] = useState(false)
	const [settingPicture, setSettingPicture] = useState(false)
	const [picture, setPicture] = useState("")
	const [uploading, setUploading] = useState(false)
	const [uploadProgress, setUploadProgress] = useState(0)
	const [uploadFiles, setUploadFiles] = useState([])
	const [filePlaceholder, setFilePlaceholder] = useState("")
	const [fileErrors, setFileErrors] = useState(0)
	const [error, setError] = useState("")

	const [resetting, setResetting] = useState(false)
	const [resettingPassword, setResettingPassword] = useState(false)
	const [newPassword, setNewPassword] = useState("")

	const [lockingAccount, setLockingAccount] = useState(false)
	const [savingLockState, setSavingLockState] = useState(false)

	// Deconstruct the user details from the props
	const { id, details } = props

	// Pull the signed in user in from the auth context
	const { user } = useContext(AuthContext)

	// Is this account currently locked out of the system
	const isLocked = details?.account_locked === true

	// Only allowlisted super admins get the lock controls, and nobody gets to
	// lock themselves out. The cloud function enforces both again server side
	const canLockAccounts =
		user?.is_super_admin && isSuperAdminAllowlisted(auth.currentUser?.uid) && id !== auth.currentUser?.uid

	// On component load
	useEffect(() => {
		// Update the users bio into the state
		setBio(details.bio)
		setPhone(details.phone)
		setPicture(details.picture)

		setManager({
			value: managers[details.manager],
			option: details.manager,
		})
	}, [])

	/**
	 * When the resettingPassword state is updated
	 */
	useEffect(() => {
		/**
		 * Clear the new password if were loading into the window
		 */
		resettingPassword && setNewPassword("")
	}, [resettingPassword])

	/**
	 * Save the toggles into the database for the given user
	 */
	const toggleEnquiries = async () => {
		// A locked account holds its permissions in the lock snapshot, so there's
		// nothing to toggle until it's unlocked
		if (isLocked) return
		/**
		 * Show a loading spinner for the save button
		 */
		setSaving(true)
		/**
		 * Save the toggles into the database
		 */
		await db.doc(`users/${id}`).set(
			{
				show_for: {
					assignable: !details.show_for.assignable,
				},
				updated: firebase.firestore.FieldValue.serverTimestamp(),
			},
			{ merge: true }
		)
		/**
		 * Reset the state
		 */
		setSaving(false)
	}

	/**
	 * Save the toggles into the database for the given user
	 */
	const toggleLeaderboard = async () => {
		// Nothing to toggle while the account is locked
		if (isLocked) return
		/**
		 * Show a loading spinner for the save button
		 */
		setSaving(true)

		const newTargetsValue = !details.show_for.targets

		/**
		 * Save the toggles into the database
		 */
		await db.doc(`users/${id}`).set(
			{
				show_for: {
					targets: newTargetsValue,
				},
				// Clear leaderboard_id if user is no longer shown on leaderboard
				...(newTargetsValue ? {} : { leaderboard_id: null }),
				updated: firebase.firestore.FieldValue.serverTimestamp(),
			},
			{ merge: true }
		)
		/**
		 * Reset the state
		 */
		setSaving(false)
	}

	/**
	 * Update the user's leaderboard assignment
	 */
	const updateLeaderboardAssignment = async (leaderboardOption) => {
		setSaving(true)

		try {
			await db.doc(`users/${id}`).set(
				{
					leaderboard_id: leaderboardOption.option,
					updated: firebase.firestore.FieldValue.serverTimestamp(),
				},
				{ merge: true }
			)
		} catch (err) {
			console.log(err)
			alert("Could not update the user's leaderboard assignment.")
		} finally {
			setSaving(false)
		}
	}

	/**
	 * Clear the user's leaderboard assignment
	 */
	const clearLeaderboardAssignment = async () => {
		setSaving(true)

		try {
			await db.doc(`users/${id}`).set(
				{
					leaderboard_id: null,
					updated: firebase.firestore.FieldValue.serverTimestamp(),
				},
				{ merge: true }
			)
		} catch (err) {
			console.log(err)
			alert("Could not clear the user's leaderboard assignment.")
		} finally {
			setSaving(false)
		}
	}

	/**
	 * Save the toggles into the database for the given user
	 */
	const toggleCanAssign = async () => {
		// Nothing to toggle while the account is locked
		if (isLocked) return
		/**
		 * Show a loading spinner for the save button
		 */
		setSaving(true)
		/**
		 * Save the toggles into the database
		 */
		await db.doc(`users/${id}`).set(
			{
				show_for: {
					can_assign: !details.show_for.can_assign,
				},
				updated: firebase.firestore.FieldValue.serverTimestamp(),
			},
			{ merge: true }
		)
		/**
		 * Reset the state
		 */
		setSaving(false)
	}

	/**
	 * Reset the users password with the new one from the state
	 */
	const processPasswordReset = async () => {
		/**
		 * Set the state to show it's loading
		 */
		setResetting(true)
		/**
		 * Call a cloud function to update the users password
		 */
		const resetUserPassword = fun.httpsCallable("resetUserPassword")
		await resetUserPassword({
			userID: id,
			password: newPassword,
		})
		/**
		 * Reset the state
		 */
		setResetting(false)
		setResettingPassword(false)
	}

	/**
	 * Lock or unlock the account. The cloud function clears (or restores) every
	 * role flag on the user document and disables (or re-enables) their Firebase
	 * auth account, so this only has to fire the call and report the outcome
	 */
	const processLockStateChange = async () => {
		setSavingLockState(true)

		try {
			const setUserLockState = fun.httpsCallable("setUserLockState")
			await setUserLockState({
				userID: id,
				locked: !isLocked,
			})
			setLockingAccount(false)
		} catch (err) {
			console.log(err)
			alert(err?.message || `Could not ${isLocked ? "unlock" : "lock"} this account.`)
		} finally {
			setSavingLockState(false)
		}
	}

	// Save the new bio for the user
	const saveUsersBio = async () => {
		// Set the loading state
		setSavingBio(true)

		// Update the database
		await db.doc(`users/${id}`).set(
			{
				bio: bio.trim(),
			},
			{ merge: true }
		)

		// Reset the state
		setSavingBio(false)
		setSettingBio(false)
	}

	// Save the phone number for the user
	const saveUsersPhone = async () => {
		// Set the loading state
		setSavingPhone(true)

		// Update the database
		await db.doc(`users/${id}`).set(
			{
				phone: phone.trim(),
			},
			{ merge: true }
		)

		// Reset the state
		setSavingPhone(false)
		setSettingPhone(false)
	}

	// Upload and save the new profile picture for the sales agent
	const saveUsersPicture = async () => {
		// Set the loading state
		setSavingPicture(true)

		// Process the file upload
		await handleFileUpload()

		// Reset the state
		setSavingPicture(false)
		setSettingPicture(false)
	}

	// Check the images chosen by the user for some requirements
	const checkChosenFiles = (files) => {
		// Set the state
		setError("")

		// Check for an empty array
		if (files.length > 0) {
			// Loop over the files
			for (const [index, file] of Object.entries(files)) {
				// Don't push this index because it'll only be holding the file count
				if (index !== "length") {
					// Generate a new ID for using on the document references for this file
					const uploadID = random.generate(20)

					// Strip everything from the name to just keep letters
					let cleanName = file.name.match(/[a-zA-Z]+/g)
					let fileID = `${cleanName}${file.size}`
					let fileUploadTime = file.lastModified
					let size = file.size

					// Add an ID and an errors object to the file
					let newFile = {
						id: fileID,
						title: file.name,
						uploadID,
						error: {},
						file,
						upload: {},
					}

					// Check if this file already exists
					uploadFiles.forEach((file) => {
						if (file.id === fileID) {
							newFile.id = `${fileID}${fileUploadTime}`
							newFile.error = {
								message: "You've already added this file to the upload.",
							}
						}
					})

					// Check for a valid file format
					const allowedFormats = ["image/jpeg", "image/jpg", "image/png"]
					if (!allowedFormats.includes(file.type)) {
						newFile.error = {
							message: "This file format is not supported.",
						}
					}

					// Check the size of the file to make sure it's below an acceptable limit of 5mb
					const sizeInMB = size / 1024 / 1024
					if (sizeInMB > 5) {
						newFile.error = {
							message: "Please ensure your file is below 5MB.",
						}
					}

					// Make sure there were no error appended onto this file
					if (!newFile.error?.message) {
						// Push the files object to the array for sending to state
						setUploadFiles((uploadFiles) => [...uploadFiles, newFile])
					} else {
						// Push the errors into the state
						setError(newFile.error?.message)
					}
				}
			}
		}
	}

	// Handles the uploading of the file into the storage bucket
	const handleFileUpload = () => {
		// Get the file object from the state
		const file = uploadFiles[0].file

		// Generate a new ID for using on the document references for this file
		const uploadID = random.generate(20)

		// Build out some metadata for the image
		const meta = {
			customMetadata: {
				fileID: uploadID,
				uploadedBy: auth.currentUser.uid,
			},
		}

		// Move the file into firebase storage
		const upload = storage.ref(`users/${id}/${uploadID}`).put(file, meta)

		// Return a promise listening to the upload progress
		return new Promise((resolve, reject) => {
			// Setup the monitor for the file
			monitorUploadProgress(upload, file.id).then(() => {
				// When it's complete, get the downloadURL from the callback
				upload.snapshot.ref.getDownloadURL().then(async (url) => {
					// Update the users document with the new image URL
					await db.doc(`users/${id}`).set(
						{
							picture: url,
						},
						{ merge: true }
					)

					// Set the image into the state
					setPicture(url)

					// And reoslve the promise
					resolve(url)
				})
			})
		})
	}

	// Attach a listener onto the upload job to stream the progress into state
	const monitorUploadProgress = (upload, fileID) => {
		return new Promise((resolve, reject) => {
			// Setup a listener on the upload process
			upload.on(
				"state_changed",
				(snapshot) => {
					// Work out a progress percentage
					const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)

					// Update the state with the progress of this file
					setUploadProgress(progress)
				},
				(error) => console.log(error),
				() => {
					resolve()
				}
			)
		})
	}

	const saveUsersNewEmail = async () => {
		setSavingEmail(true)

		try {
			const changeEmailFunction = fun.httpsCallable("updateAuthEmail")
			await changeEmailFunction({
				userID: id,
				email: newEmail,
			})
		} catch (err) {
			console.log(err)
			alert("Could not update the users email.")
		} finally {
			setNewEmail("")
			setSavingEmail(false)
			setChangeEmail(false)
			alert("Email updated successfully")
		}
	}

	const updateUsersManager = async (managerOpt) => {
		// Locked accounts aren't editable until they're unlocked
		if (isLocked) return

		setSaving(true)

		try {
			await db.doc(`users/${id}`).set(
				{
					manager: managerOpt.option,
				},
				{ merge: true }
			)
			setManager({
				value: managerOpt.value,
				option: managerOpt.option,
			})
		} catch (err) {
			console.log(err)
			alert("Could not update the users manager.")
		} finally {
			setSaving(false)
			alert("Manager updated successfully")
		}
	}

	const clearManager = async () => {
		setClearingManager(true)
		await db.doc(`users/${id}`).set(
			{
				manager: null,
			},
			{ merge: true }
		)
		setManager({})
		setClearingManager(false)
	}

	/**
	 * Copy user ID to clipboard
	 */
	const copyUserId = () => {
		navigator.clipboard.writeText(props.id)
	}

	/**
	 * Check if current user can manage this user (for showing all actions)
	 */
	const isOwnAccount =
		(auth.currentUser.uid === "CGOHCdUwFAOe13OSTY2D9qdO2LX2" && props.id === "CGOHCdUwFAOe13OSTY2D9qdO2LX2") ||
		(auth.currentUser.uid === "iUlTXjfx1OZEPBhPosh6rJt3CKe2" && props.id === "iUlTXjfx1OZEPBhPosh6rJt3CKe2")

	const canClearManager = props.id !== "CGOHCdUwFAOe13OSTY2D9qdO2LX2" && props.id !== "iUlTXjfx1OZEPBhPosh6rJt3CKe2"

	return (
		<>
			<div
				className={["user-card", isLocked && "is-locked"].filter(Boolean).join(" ")}
				data-system-user={props.id}>
				{/* User header with avatar and name */}
				<div className="user-card-header">
					<div className="user-avatar">
						{picture ? (
							<img
								src={picture}
								alt={`${details.first_name} ${details.last_name}`}
							/>
						) : (
							<UserIcon />
						)}
					</div>
					<div className="user-info">
						<h3>
							{details.first_name} {details.last_name}
						</h3>
						<div className="user-meta">
							<span
								className="user-id"
								onClick={copyUserId}
								title="Click to copy">
								<CopyIcon />
								{props.id.substring(0, 12)}...
							</span>
							<span className="user-version">v{props.details?.on_version || "?"}</span>
							<span className={["user-status", isLocked ? "is-locked" : "is-active"].join(" ")}>
								{isLocked ? "Locked" : "Active"}
							</span>
						</div>
					</div>
				</div>

				{/* Card body with sections */}
				<div className={["user-card-body", isLocked && "is-locked"].filter(Boolean).join(" ")}>
					{/* Display options section */}
					<div className="user-card-section">
						<h4>Display Options</h4>
						<div className="checkbox-group">
							<Checkbox
								checked={details?.show_for.can_assign}
								onClick={() => toggleCanAssign()}
								label="Can assign enquiries"
							/>
							<Checkbox
								checked={details?.show_for.assignable}
								onClick={() => toggleEnquiries()}
								label="Can be assigned"
							/>
							<Checkbox
								checked={details?.show_for.targets}
								onClick={() => toggleLeaderboard()}
								label="Show on leaderboard"
							/>
						</div>

						{/* Leaderboard assignment dropdown - only show when user is on leaderboard */}
						{details?.show_for?.targets && (
							<div className="leaderboard-assignment">
								<Select
									placeholder="Choose leaderboard:"
									value={details?.leaderboard_id ? details.leaderboard_id : "No team assigned"}
									selected={details?.leaderboard_id}
									activeOnHover={true}
									onSelect={(option) => updateLeaderboardAssignment(option)}
									options={{
										"GHD-MAIN-01": "GHD-MAIN-01",
										"GHD-MAIN-02": "GHD-MAIN-02",
										"GHD-MAIN-03": "GHD-MAIN-03",
									}}
								/>
								{details?.leaderboard_id && (
									<Button
										xsSmall={true}
										label="Clear"
										onClick={() => clearLeaderboardAssignment()}
									/>
								)}
							</div>
						)}
					</div>

					{/* Manager section */}
					<div className="user-card-section">
						<h4>Manager</h4>
						<Select
							placeholder="Choose manager:"
							value={manager?.value}
							selected={manager?.option}
							activeOnHover={true}
							onSelect={(option) => updateUsersManager(option)}
							options={{
								M1EUhnht2mQc9pucFAB7A8rWFul1: "Jack Allen",
								"8lIXQuliWhPJXrgi0rM7dJ3e9kM2": "James Bilotta",
								gWd7pXtuAMcXrywMUTw2vRbid1j1: "Josh Drury",
								p4VG2EgFFQS8fsaBc0d9peNWaD52: "Matt Hill",
								L334Rwj0YiRWSuCZLlixoBbsxwl2: "Oliver Howell",
							}}
						/>
					</div>
				</div>

				{/* Actions footer */}
				<div className="user-card-actions">
					<button
						className="action-btn"
						onClick={() => setSettingBio(true)}
						disabled={isLocked}
						title="Set bio">
						<PenIcon />
						<span>Bio</span>
					</button>
					<button
						className="action-btn"
						onClick={() => setSettingPhone(true)}
						disabled={isLocked}
						title="Set phone">
						<GlobeIcon />
						<span>Phone</span>
					</button>
					<button
						className="action-btn"
						onClick={() => setSettingPicture(true)}
						disabled={isLocked}
						title="Set picture">
						<ImageIcon />
						<span>Picture</span>
					</button>
					<button
						className="action-btn"
						onClick={() => setResettingPassword(true)}
						disabled={isLocked}
						title="Reset password">
						<EyeIcon />
						<span>Password</span>
					</button>
					<button
						className="action-btn"
						onClick={() => setChangeEmail(true)}
						disabled={isLocked}
						title="Change email">
						<UserIcon />
						<span>Email</span>
					</button>
					{canClearManager && manager?.option && !isLocked && (
						<button
							className="action-btn danger"
							onClick={() => clearManager()}
							disabled={clearingManager}
							title="Clear manager">
							<span>{clearingManager ? "..." : "Clear Mgr"}</span>
						</button>
					)}
					{/* Locking an account is a super admin only action */}
					{canLockAccounts && (
						<button
							className={["action-btn", isLocked ? "positive" : "danger"].join(" ")}
							onClick={() => setLockingAccount(true)}
							title={isLocked ? "Unlock this account" : "Lock this account"}>
							{isLocked ? <UndoIcon /> : <CancelIcon />}
							<span>{isLocked ? "Unlock" : "Lock"}</span>
						</button>
					)}
				</div>
			</div>

			{settingBio && (
				<Window
					className="slim"
					title={`Setting ${details.first_name}'s bio`}
					close={() => setSettingBio(false)}>
					<WindowCard>
						<div className="input-grid cols-1">
							<Textarea
								placeholder="e.g. I’ve been working within the golf travel industry for x years..."
								value={bio}
								onChange={setBio}
							/>

							<Button
								label="Save"
								loading={savingBio}
								loadingText="Saving..."
								fullWidth={true}
								onClick={() => saveUsersBio()}
							/>
						</div>
					</WindowCard>
				</Window>
			)}

			{settingPhone && (
				<Window
					className="slim"
					title={`Setting ${details.first_name}'s phone number`}
					close={() => setSettingPhone(false)}>
					<WindowCard>
						<div className="input-grid cols-1">
							<Input
								placeholder="e.g. 01234 567890"
								value={phone}
								onChange={setPhone}
							/>

							<Button
								label="Save"
								loading={savingBio}
								loadingText="Saving..."
								fullWidth={true}
								onClick={() => saveUsersPhone()}
							/>
						</div>
					</WindowCard>
				</Window>
			)}

			{changeEmail && (
				<Window
					className="slim"
					title={`Setting ${details.first_name}'s email address`}
					close={() => setChangeEmail(false)}>
					<WindowCard>
						<div className="input-grid cols-1">
							<Input
								placeholder="New email address:"
								value={newEmail}
								onChange={setNewEmail}
							/>

							<Button
								label="Update"
								loading={savingEmail}
								loadingText="Saving..."
								fullWidth={true}
								onClick={() => saveUsersNewEmail()}
							/>
						</div>
					</WindowCard>
				</Window>
			)}

			{settingPicture && (
				<Window
					className="slim"
					title={`Update ${details.first_name}'s picture`}
					close={() => setSettingPicture(false)}>
					<WindowCard>
						<div className="input-grid cols-1">
							<div className="user-picture-flex">
								{picture && (
									<a
										href={picture}
										target="_blank"
										className="user-profile-picture">
										<img
											src={picture}
											alt={details.first_name}
										/>
									</a>
								)}

								<File
									placeholder="Choose file"
									showFileNames={true}
									accept="image/png, image/jpg, image/jpeg"
									onChange={(files) => checkChosenFiles(files)}
									error={error}
								/>
							</div>

							<Button
								label="Upload & Save"
								loading={savingPicture}
								loadingText="Saving..."
								fullWidth={true}
								onClick={() => saveUsersPicture()}
							/>
						</div>
					</WindowCard>
				</Window>
			)}

			{lockingAccount && (
				<Window
					className="slim"
					title={`${isLocked ? "Unlock" : "Lock"} ${details.first_name} ${details.last_name}`}
					close={() => setLockingAccount(false)}>
					<WindowCard>
						<div className="input-grid cols-1">
							{isLocked && (
								<p className="lock-window-copy">
									Unlocking re-enables {details.first_name}'s sign in and puts back every role they
									held when the account was locked.
								</p>
							)}

							<Button
								label={isLocked ? "Unlock account" : "Lock account"}
								className={isLocked ? "" : "NEGATIVE"}
								loading={savingLockState}
								loadingText={isLocked ? "Unlocking..." : "Locking..."}
								fullWidth={true}
								onClick={() => processLockStateChange()}
							/>
						</div>
					</WindowCard>
				</Window>
			)}

			{resettingPassword && (
				<Window
					className="slim"
					title={`Resetting ${details.first_name}'s password`}
					close={() => setResettingPassword(false)}>
					<WindowCard>
						<div className="input-grid cols-1">
							<Input
								type="password"
								placeholder="New password:"
								value={newPassword}
								onChange={setNewPassword}
							/>

							<Button
								label="Reset"
								loading={resetting}
								loadingText="Resetting..."
								fullWidth={true}
								disabled={newPassword.length < 8}
								onClick={() => processPasswordReset()}
							/>
						</div>
					</WindowCard>
				</Window>
			)}
		</>
	)
}
